import {
  err,
  ok,
  parseStoryboardVtt,
  resolveInstanceUrl,
  type AbortLike,
  type Result,
  type StoryboardCue,
  type StoryboardSpec,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import type { ApiProblem } from './videos';

/**
 * §7's scrub previews. The URL comes from `PlaybackPlan.source.storyboardVttUrl`, already resolved
 * to the instance (R2), and the body is WebVTT rather than JSON — which is the only thing that makes
 * this different from `fetchVideoDetails`.
 *
 * It goes through the same transport as everything else on purpose: retry and backoff are §3's
 * policy for *the instance*, not for one endpoint, and a preview track fetched around them would
 * keep hammering an instance that is already failing.
 */
export async function fetchStoryboardCues(
  platform: Platform,
  vttUrl: string,
  options: { readonly spec: StoryboardSpec | null; readonly durationSec: number | null },
  signal?: AbortLike,
): Promise<Result<readonly StoryboardCue[], ApiProblem>> {
  const response = await platform.transport.send({
    method: 'GET',
    url: vttUrl,
    headers: {},
    signal,
  });
  if (!response.ok) return err({ kind: 'transport', error: response.error });

  // R4 — a malformed cue drops itself and is counted; the track as a whole is still usable, and a
  // preview is not worth failing a page over. `dropped` is ignored rather than surfaced because a
  // missing thumbnail is invisible by nature: there is no empty state for it to render.
  const parsed = parseStoryboardVtt(response.value.body, platform.parseContext, {
    spec: options.spec,
    durationSec: options.durationSec,
  });
  return ok(parsed.items);
}

/**
 * The largest tile level served, and the one a ~360 px grid card wants. Measured 2026-08-02: four
 * levels exist (48×27 · 80×45 · 160×90 · 320×180) and the parameters select the nearest.
 */
const GRID_TRACK_QUERY = 'width=320&height=180';

/**
 * The grid preview's track (2026-08-02) — a card has a video id and no payload, and the
 * standalone endpoint answers the same finished VTT the payload's `storyboards[].url` points at.
 * **The parameters are load-bearing**: without them the endpoint answers `200 application/json`
 * (the raw level list), which parses as a track with zero cues — a silently absent preview, not an
 * error. A bogus id answers `404`; a live video has no track, and both collapse to "no preview".
 *
 * There is no `spec` to clamp with here, so the card's own `lengthSeconds` (when its record knows
 * one) is the whole defence against the over-emitted blank tail the payload route clamps by
 * `count × interval`.
 */
export async function fetchGridStoryboardCues(
  platform: Platform,
  videoId: string,
  durationSec: number | null,
  signal?: AbortLike,
): Promise<Result<readonly StoryboardCue[], ApiProblem>> {
  const path = `/api/v1/storyboards/${encodeURIComponent(videoId)}?${GRID_TRACK_QUERY}`;
  const url = resolveInstanceUrl(platform.config, path);
  if (url === null) return err({ kind: 'bad-url', path });
  return fetchStoryboardCues(platform, url, { spec: null, durationSec }, signal);
}
